import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { instructorApi } from '../../api/instructor';
import type { LearningBooklet, LearningProgression } from '../../types';
import { Accordion, Card, cx } from '../../components/charte';

// Référentiel de compétences du livret d'apprentissage (sorti de LivretPage.tsx le 30/10/2026).

// Progression renseignée par le moniteur (éditable).
const PROGRESSION_STEPS: { value: LearningProgression; label: string }[] = [
  { value: 'aborde', label: 'Abordé' },
  { value: 'traite', label: 'Traité' },
  { value: 'assimile', label: 'Assimilé' },
];
const PROGRESSION_RANK: Record<string, number> = { aborde: 1, traite: 2, assimile: 3 };

// Auto-évaluation de l'élève.
const SELF_ICON: Record<number, string> = { 1: '🙁', 2: '😐', 3: '🙂' };
const SELF_LABEL: Record<number, string> = { 1: 'Peu sûr', 2: 'À confirmer', 3: "À l'aise" };

export function ReferentialBooklet({ studentId }: { studentId: string }) {
  const queryClient = useQueryClient();
  const { data, isLoading, isError } = useQuery({
    queryKey: ['student', studentId, 'learning-booklet'],
    queryFn: () => instructorApi.getStudentLearningBooklet(studentId),
  });

  const mutation = useMutation({
    mutationFn: (vars: { itemCode: string; progression: LearningProgression | null }) =>
      instructorApi.setStudentLearningProgression(studentId, vars.itemCode, vars.progression),
    onSuccess: (fresh: LearningBooklet) => {
      queryClient.setQueryData(['student', studentId, 'learning-booklet'], fresh);
    },
  });

  if (isLoading) return <p className="text-sm text-secondary">Chargement du référentiel…</p>;
  if (isError || !data) return null;

  return (
    <Card className="space-y-4 p-5 sm:p-6">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h2 className="text-title-sm text-heading">Référentiel de compétences</h2>
          <p className="text-[13px] text-secondary">{data.referentialLabel} · renseignez la progression de l'élève, enregistrée à chaque choix</p>
          {/* L'emoji à droite de chaque compétence : ressenti de l'élève (son auto-évaluation), lecture seule. */}
          <p className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 rounded-xl bg-surface-light px-3 py-2 text-xs text-secondary">
            <span className="font-medium text-body">Ressenti de l'élève :</span>
            {[1, 2, 3].map((n) => (
              <span key={n} className="inline-flex items-center gap-1">
                <span className="text-base" aria-hidden>
                  {SELF_ICON[n]}
                </span>
                {SELF_LABEL[n].toLowerCase()}
              </span>
            ))}
            <span className="inline-flex items-center gap-1">
              <span className="text-base text-slate-300" aria-hidden>
                –
              </span>
              pas encore évalué
            </span>
          </p>
        </div>
        <p className="text-[28px] font-semibold leading-none tracking-[-1px] text-primary">{data.overallPct}%</p>
      </div>
      <div className="space-y-2">
        {data.categories.map((cat, index) => (
          <Accordion
            key={cat.code}
            defaultOpen={index === data.categories.findIndex((c) => c.progressPct < 100)}
            leading={<span className="text-sm font-bold text-primary">{cat.code}</span>}
            title={cat.title}
            meta={<span className="text-xs font-semibold tabular-nums text-primary-600">{cat.progressPct}%</span>}
          >
            <ul className="divide-y divide-slate-100">
              {cat.items.map((item) => {
                const rank = item.progression ? PROGRESSION_RANK[item.progression] : 0;
                return (
                  <li key={item.code} className="flex flex-wrap items-center justify-between gap-2 py-2.5">
                    <span className="min-w-[180px] flex-1 text-sm text-body">{item.label}</span>
                    <div className="flex shrink-0 items-center gap-2">
                      <div className="inline-flex overflow-hidden rounded-pill border border-line" role="group" aria-label={item.label}>
                        {PROGRESSION_STEPS.map((step) => {
                          const active = rank >= PROGRESSION_RANK[step.value];
                          return (
                            <button
                              key={step.value}
                              type="button"
                              disabled={mutation.isPending}
                              aria-pressed={item.progression === step.value}
                              onClick={() =>
                                mutation.mutate({
                                  itemCode: item.code,
                                  // Re-toucher le niveau courant le retire.
                                  progression: item.progression === step.value ? null : step.value,
                                })
                              }
                              className={cx(
                                'h-9 border-l border-line px-3 text-[12.5px] font-semibold transition-colors first:border-l-0 disabled:opacity-60',
                                active ? 'bg-primary text-white' : 'bg-white text-secondary hover:bg-primary-50',
                              )}
                            >
                              {step.label}
                            </button>
                          );
                        })}
                      </div>
                      <span
                        className={cx('w-6 text-center text-lg', !item.selfAssessment && 'text-slate-300')}
                        role="img"
                        aria-label={item.selfAssessment ? `Ressenti de l'élève : ${SELF_LABEL[item.selfAssessment]}` : "Ressenti de l'élève : pas encore évalué"}
                        title={item.selfAssessment ? `Ressenti de l'élève : ${SELF_LABEL[item.selfAssessment]}` : "Ressenti de l'élève : pas encore évalué"}
                      >
                        {item.selfAssessment ? SELF_ICON[item.selfAssessment] : '–'}
                      </span>
                    </div>
                  </li>
                );
              })}
            </ul>
          </Accordion>
        ))}
      </div>
    </Card>
  );
}
